// CS-1766 - Product Shipping Methods Configuration Component
// Displays all master shipping methods in an inline table using CustomTable.
// Users can toggle custom pricing per method and enter product-specific prices.
// Non-toggled methods retain their default global price.
// Access is controlled via canAccessProductShipping token in Product.tsx.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button as MuiButton, Chip, Switch, TextField, Typography, useTheme } from "@mui/material";
import { CancelOutlined, FiberManualRecord, SaveOutlined } from "@mui/icons-material";
import { NumericFormat } from "react-number-format";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import CustomTable from "../../common/CustomTable";
import CustomTooltip from "../../common/CustomTooltip";
import { useModalStyles } from "../../common/useModalStyles";
import { tokens } from "../../../theme";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useNavigation } from "../../../hooks/useNavigation";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
// CS-1766 - Import shipping config thunks and selector
import { fetchProductShippingConfig, saveProductShippingConfigThunk, useProductReducer } from "../../../redux/slice/product/createProductSlice";
import Button from "../../common/Button";

// CS-1766 - Shape of a shipping method mapped from API response
export interface ShippingMethodMaster {
  shippingConfigId: string;
  productShippingConfigId: string | null;
  name: string;
  carrier: string;
  defaultRate: number;
  isEnabled: boolean;
  activeRate: number;
  useDefaultRate: boolean;
}

// CS-1766 - Custom toggle and price state types
type CustomToggles = Record<string, boolean>;
type CustomPrices = Record<string, string>;

interface ProductShippingConfigProps {
  canEdit: boolean; // CS-1766 - Controls whether toggles/inputs are interactive
  productId: number; // CS-1766 - Product ID for fetching shipping config
  embedded?: boolean;
  /** Optional section title rendered inline (same row) with the toolbar. */
  title?: React.ReactNode;
  titleIcon?: React.ReactNode;
}

const ProductShippingConfig: React.FC<ProductShippingConfigProps> = ({ canEdit, productId, embedded = false, title, titleIcon }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { tokens: modalTokens, primaryBtnSx, cancelBtnSx } = useModalStyles();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();

  // CS-1766 - Get shipping config data from redux store
  const { productShippingConfig, fetchProductShippingConfigStatus, saveProductShippingConfigStatus } = useProductReducer();

  // CS-1766 - Fetch shipping config when productId is available
  useEffect(() => {
    if (productId > 0) {
      dispatch(fetchProductShippingConfig({ productId }));
    }
  }, [productId, dispatch]);

  // CS-1766 - Map API response to ShippingMethodMaster format
  const shippingMethods = useMemo<ShippingMethodMaster[]>(() => {
    if (!productShippingConfig || productShippingConfig.length === 0) return [];
    return productShippingConfig.map((config: any) => ({
      shippingConfigId: String(config.shippingConfigId),
      productShippingConfigId: config.productShippingConfigId ? String(config.productShippingConfigId) : null,
      name: config.name ?? "",
      carrier: config.carrier ?? "-",
      defaultRate: config.defaultRate ?? 0,
      isEnabled: config.isEnabled ?? false,
      activeRate: config.activeRate ?? 0,
      useDefaultRate: config.useDefaultRate ?? true,
    }));
  }, [productShippingConfig]);

  // CS-1766 - Track which methods have custom pricing enabled
  const [customToggles, setCustomToggles] = useState<CustomToggles>({});
  // CS-1766 - Track custom price values per method
  const [customPrices, setCustomPrices] = useState<CustomPrices>({});

  // CS-1766 - Initialize toggles and prices from API response
  useEffect(() => {
    if (productShippingConfig && productShippingConfig.length > 0) {
      const toggles: CustomToggles = {};
      const prices: CustomPrices = {};
      productShippingConfig.forEach((config: any) => {
        const key = String(config.shippingConfigId);
        if (config.isEnabled) {
          toggles[key] = true;
        }
        if (!config.useDefaultRate && config.activeRate != null) {
          prices[key] = String(config.activeRate ?? "");
        }
      });
      setCustomToggles(toggles);
      setCustomPrices(prices);
    }
  }, [productShippingConfig]);

  // CS-1766 - Count of active shipping methods
  const activeCount = useMemo(
    () => Object.values(customToggles).filter(Boolean).length,
    [customToggles]
  );

  // CS-1766 - Toggle custom pricing for a shipping method
  const handleToggleCustom = useCallback((methodId: string, checked: boolean) => {
    setCustomToggles((prev) => ({ ...prev, [methodId]: checked }));
    if (!checked) {
      setCustomPrices((prev) => {
        const next = { ...prev };
        delete next[methodId];
        return next;
      });
    }
  }, []);

  // CS-1766 - Update custom price for a shipping method
  const handlePriceChange = useCallback((methodId: string, value: string) => {
    setCustomPrices((prev) => ({ ...prev, [methodId]: value }));
  }, []);

  // CS-1766 - Save shipping configuration via API
  const handleSaveShipping = useCallback(async () => {
    // CS-1766 - Only include configs where the user changed toggle or rate
    const configs: { shippingConfigId: number; isEnabled: boolean; rate?: number }[] = [];

    shippingMethods.forEach((method) => {
      const key = method.shippingConfigId;
      const currentEnabled = !!customToggles[key];
      const originalEnabled = method.isEnabled;
      const priceVal = customPrices[key];
      const currentRate = currentEnabled && priceVal ? parseFloat(priceVal) : null;
      const originalRate = !method.useDefaultRate && method.activeRate != null ? method.activeRate : null;

      // CS-1766 - Detect if toggle or rate changed from original API values
      const toggleChanged = currentEnabled !== originalEnabled;
      const rateChanged = currentRate !== originalRate;

      if (toggleChanged || rateChanged) {
        const configItem: { shippingConfigId: number; isEnabled: boolean; rate?: number } = {
          shippingConfigId: Number(key),
          isEnabled: currentEnabled,
        };
        // CS-1766 - Only include rate field if user entered a value
        if (currentRate != null) {
          configItem.rate = currentRate;
        }
        configs.push(configItem);
      }
    });

    if (configs.length === 0) {
      dispatch(setSingleAlertObj({ message: "No changes to save.", alertType: "info" }));
      return;
    }

    const payload = { productId, configs };
    const response = await dispatch(saveProductShippingConfigThunk({ payload }));

    if (response.meta.requestStatus === "fulfilled") {
      const alertObj: SingleAlertInfo = {
        message: "Shipping configuration saved successfully.",
        alertType: "success",
      };
      dispatch(setSingleAlertObj(alertObj));
      // CS-1766 - Re-fetch to sync latest data from server
      dispatch(fetchProductShippingConfig({ productId }));
    }
  }, [shippingMethods, customToggles, customPrices, productId, dispatch]);

  // CS-1766 - Column definitions for CustomTable
  const shippingColumns = useMemo(() => [
    { id: "carrier", label: "Carrier" },
    { id: "method", label: "Method" },
    { id: "defaultRate", label: "Default Price" },
    { id: "isSizeActive", label: "Active" },
    { id: "action", label: "Active Price" },
  ], []);

  // CS-1766 - Build rows for CustomTable from shipping methods
  const shippingRows = useMemo(() =>
    shippingMethods.map((method) => ({
      uniqueId: method.shippingConfigId,
      _method: method,
      carrier: method.carrier,
      method: method.name,
      defaultRate: <CustomCurrencyField externalValue={method.defaultRate} displayType="text" />,
    })),
  [shippingMethods]);

  // CS-1766 - Render custom toggle switch with tooltip (mapped to isSizeActive column)
  const renderCustomToggle = useCallback((row: any) => {
    const method: ShippingMethodMaster = row._method;
    const isActive = !!customToggles[method.shippingConfigId];
    return (
      <CustomTooltip title={isActive ? "Active" : "Inactive"} arrow placement="top">
        <span>
          <Switch
            checked={isActive}
            onChange={(e) => handleToggleCustom(method.shippingConfigId, e.target.checked)}
            disabled={!canEdit}
            size="small"
            sx={{
              "& .MuiSwitch-switchBase": { color: theme.palette.error.main },
              "& .MuiSwitch-switchBase + .MuiSwitch-track": { backgroundColor: theme.palette.error.main },
              "& .MuiSwitch-switchBase.Mui-checked": { color: theme.palette.success.main },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: theme.palette.success.main },
            }}
          />
        </span>
      </CustomTooltip>
    );
  }, [canEdit, customToggles, handleToggleCustom, theme.palette.error.main, theme.palette.success.main]);

  // CS-1766 - Render active price input cell (mapped to action column)
  // Show "N/A" when the shipping method is inactive.
  // In view-only mode, display the active rate as plain text similar to default price.
  // In edit mode, show the editable TextField.
  const renderActivePrice = useCallback((row: any) => {
    const method: ShippingMethodMaster = row._method;
    const isCustom = !!customToggles[method.shippingConfigId];
    const customVal = customPrices[method.shippingConfigId] ?? String(method.activeRate ?? "");

    // CS-1766 - Inactive shipping methods should not display the default/active price in the table.
    if (!isCustom) {
      return (
        <Box display="flex" alignItems="center">
          <Typography component="span">N/A</Typography>
        </Box>
      );
    }

    // CS-1766 - View-only mode: display active price as formatted currency text
    if (!canEdit) {
      return (
        <CustomCurrencyField externalValue={customVal || "0"} displayType="text" />
      );
    }

    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography component="span" sx={{ opacity: 0.5 }}>$</Typography>
        <NumericFormat
          customInput={TextField}
          value={customVal}
          onValueChange={(vals) => handlePriceChange(method.shippingConfigId, vals.value)}
          allowNegative={false}
          fixedDecimalScale
          decimalScale={2}
          thousandSeparator
          size="small"
          variant="outlined"
          sx={{
            width: 90,
            "& .MuiOutlinedInput-root": {
              height: 30,
              "& input": { py: 0.5, px: 1 },
              "& fieldset": { borderColor: "rgba(255, 152, 0, 0.3)" },
              "&:hover fieldset": { borderColor: "rgba(255, 152, 0, 0.5)" },
              "&.Mui-focused fieldset": { borderColor: "#ffb74d" },
            },
          }}
        />
      </Box>
    );
  }, [canEdit, customPrices, customToggles, handlePriceChange]);

  const isDark = theme.palette.mode === "dark";
  // Matches the OrderList table look — cyan headers, subtle row dividers, teal hover.
  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";
  const tableSx = embedded
    ? {
        border: `1px solid ${modalTokens.border}`,
        borderRadius: "10px",
        overflow: "hidden",
        p: 0,
        "& .MuiTableContainer-root": { borderRadius: "10px", border: "none", backgroundColor: "transparent" },
        "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
        "& .MuiTableCell-root": { borderBottom: `1px solid ${listBorderColor}`, py: 1.15, fontSize: "0.84rem" },
        "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
        "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
        "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
        "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
        "& .MuiTableBody-root .MuiTableCell-root": { color: modalTokens.textPrimary, fontSize: "0.84rem" },
        "& .MuiTableBody-root .MuiTypography-root": { fontSize: "0.84rem" },
        "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: modalTokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
        "& .MuiPaginationItem-root": { borderColor: listBorderColor, color: modalTokens.textSecondary, borderRadius: "8px", "&.Mui-selected": { color: "#fff", backgroundColor: modalTokens.accent, borderColor: modalTokens.accent } },
      }
    : undefined;

  return (
    <Box>
      {/* CS-1766 - Toolbar with legend icons and action buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: title ? "space-between" : "flex-end",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
          ...(embedded
            ? {}
            : {
                borderRadius: 1,
                boxShadow: 1,
                p: 1,
                backgroundColor: colors.grey[900],
              }),
        }}
      >
        {title && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {titleIcon}
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, color: modalTokens.textPrimary, letterSpacing: "-0.01em" }}>
              {title}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.7,
            px: 1.2,
            py: 0.5,
            borderRadius: "999px",
            border: `1px solid ${modalTokens.border}`,
            backgroundColor: modalTokens.inputBg,
          }}
        >
          <FiberManualRecord sx={{ fontSize: 9, color: "#ef4444" }} />
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: modalTokens.textPrimary }}>Inactive</Typography>
        </Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.7,
            px: 1.2,
            py: 0.5,
            borderRadius: "999px",
            border: `1px solid ${modalTokens.border}`,
            backgroundColor: modalTokens.inputBg,
          }}
        >
          <FiberManualRecord sx={{ fontSize: 9, color: "#22c55e" }} />
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: modalTokens.textPrimary }}>Active</Typography>
        </Box>
        {activeCount > 0 && (
          <Chip
            label={`${activeCount} Active`}
            size="small"
            sx={{
              backgroundColor: "rgba(76, 175, 80, 0.15)",
              color: theme.palette.success.main,
              fontSize: "0.7rem",
              height: 22,
              fontWeight: 600,
            }}
          />
        )}
        {canEdit && (
          <MuiButton
            type="button"
            variant="contained"
            disableElevation
            data-testid="save-shipping-btn"
            startIcon={<SaveOutlined sx={{ fontSize: "1rem !important" }} />}
            onClick={handleSaveShipping}
            disabled={saveProductShippingConfigStatus === "loading"}
            sx={{
              ...primaryBtnSx,
              py: 0.8,
              px: 1.6,
              fontSize: "0.82rem",
            }}
          >
            Save Shipping
          </MuiButton>
        )}
        </Box>
      </Box>

      {/* CS-1766 - Shipping methods table */}
      <CustomTable
        columns={shippingColumns}
        rows={shippingRows}
        disablePagination
        borderBox={!embedded}
        emptyText={fetchProductShippingConfigStatus === "loading" ? "Loading..." : "No Shipping Method(s) Found"}
        showMenu={false}
        availabilitySwitchComponent={(row: any) => renderCustomToggle(row)}
        actionComponent={(row: any) => renderActivePrice(row)}
        sx={tableSx}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
        <Button
          color="error"
          variant="outlined"
          startIcon={<CancelOutlined sx={{ fontSize: "1rem !important" }} />}
          onClick={() => navigate("/products/explore_market")}
          sx={{
            ...cancelBtnSx,
            fontSize: "0.78rem",
            py: 1,
            px: 1.5,
            minWidth: "auto",
            borderRadius: "8px",
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default ProductShippingConfig;
